import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { X, Download } from 'lucide-react';

function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const handleInstallPrompt = (e) => {
      setDeferredPrompt(e.detail);
      setShowPrompt(true);
    };

    window.addEventListener('pwaInstallAvailable', handleInstallPrompt);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(false);
    }

    return () => {
      window.removeEventListener('pwaInstallAvailable', handleInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for 7 days
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  // Check if user dismissed recently
  useEffect(() => {
    const dismissed = localStorage.getItem('pwaPromptDismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < sevenDays) {
        setShowPrompt(false);
      }
    }
  }, []);

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96" data-testid="pwa-install-prompt">
      <Card className="bg-white shadow-2xl border-2 border-emerald-200">
        <div className="p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
            aria-label="Dismiss"
            data-testid="dismiss-pwa-prompt"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-emerald-900 rounded-xl flex items-center justify-center">
              <Download className="w-6 h-6 text-white" />
            </div>
            
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 mb-1">Install CaddiePay</h3>
              <p className="text-sm text-slate-600 mb-4">
                Install our app for faster access, offline support, and a better experience!
              </p>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleInstallClick}
                  className="btn-primary text-sm h-9"
                  data-testid="install-pwa-btn"
                >
                  Install App
                </Button>
                <Button
                  onClick={handleDismiss}
                  variant="outline"
                  className="text-sm h-9"
                  data-testid="maybe-later-btn"
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default PWAInstallPrompt;